import { useEffect, useState, useCallback } from 'react';
import {
  useMediaPermissions,
  PermissionState,
  createLogger,
  testFirefoxAndroidPermissions,
  isFirefoxAndroid,
} from '@common/media';
import { InterviewType } from '@common/ts/enums';
import { iframeLocalStorage } from 'chat-ui/src/utils/iframeLocalStorage';
import { useMediaTest } from './useMediaTest';

const PERMISSION_MODAL_SHOWN_KEY = 'permission_modal_shown';

// Create logger instance for permission dialogs
const logger = createLogger(true);

interface UsePermissionDialogsProps {
  chatSettingsData?: string[];
  isTestChat?: boolean;
}

export function usePermissionDialogs({ chatSettingsData, isTestChat }: UsePermissionDialogsProps) {
  // Media permissions check
  const { permissions, isLoading: isPermissionsLoading, refresh } = useMediaPermissions();

  // Media test functionality
  const mediaTest = useMediaTest();

  // Check if video/audio features are enabled
  const videoEnabled = chatSettingsData?.includes(InterviewType.VIDEO);
  const audioEnabled = chatSettingsData?.includes(InterviewType.AUDIO);

  // Determine if permission dialog should show
  const hasMediaFeatures = videoEnabled || audioEnabled;
  const permissionsReady = !isPermissionsLoading && permissions;

  const cameraPermissionNeeded = videoEnabled && permissions?.camera !== PermissionState.GRANTED;
  const microphonePermissionNeeded =
    audioEnabled && permissions?.microphone !== PermissionState.GRANTED;
  const anyPermissionNeeded = cameraPermissionNeeded || microphonePermissionNeeded;

  // Permission modal state
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [requestedPermissions, setRequestedPermissions] = useState<{
    video?: boolean;
    audio?: boolean;
  }>({});
  const [isGrantingAccess, setIsGrantingAccess] = useState(false);
  const [autoDismissed, setAutoDismissed] = useState(() => {
    return iframeLocalStorage.getItem(PERMISSION_MODAL_SHOWN_KEY) === 'true';
  });

  // Auto-show dialog on load if permissions not granted (and not manually dismissed, and not in test mode)
  const needsPermissionDialog = !!(
    permissionsReady &&
    hasMediaFeatures &&
    anyPermissionNeeded &&
    !autoDismissed &&
    !isTestChat
  );

  useEffect(() => {
    if (needsPermissionDialog && !showPermissionModal) {
      setShowPermissionModal(true);
    }
  }, [needsPermissionDialog, showPermissionModal]);

  // Helper function to request specific permissions
  const requestPermissions = useCallback(
    (includeVideo: boolean = false, includeAudio: boolean = false) => {
      const needsVideo =
        includeVideo && videoEnabled && permissions?.camera !== PermissionState.GRANTED;
      const needsAudio =
        includeAudio && audioEnabled && permissions?.microphone !== PermissionState.GRANTED;

      if (needsVideo || needsAudio) {
        setRequestedPermissions({
          video: needsVideo,
          audio: needsAudio,
        });
        setShowPermissionModal(true);
        setAutoDismissed(true); // Disable auto-dialog when manually triggered
      }
    },
    [videoEnabled, audioEnabled, permissions?.camera, permissions?.microphone],
  );

  // Functions to trigger permission modal from ChatInput
  const requestCameraPermission = useCallback(() => {
    // For camera, also include audio if it's enabled and not granted
    requestPermissions(true, true);
  }, [requestPermissions]);

  const requestMicrophonePermission = useCallback(() => {
    requestPermissions(false, true);
  }, [requestPermissions]);

  const requestBothPermissions = useCallback(() => {
    requestPermissions(true, true);
  }, [requestPermissions]);

  // Handle permission request
  const handleGrantAccess = async () => {
    setIsGrantingAccess(true);

    try {
      const constraints: MediaStreamConstraints = {};

      // For auto-shown dialog, request all needed permissions
      if (needsPermissionDialog) {
        if (cameraPermissionNeeded) {
          constraints.video = true;
        }
        if (microphonePermissionNeeded) {
          constraints.audio = true;
        }
      } else {
        // For manually triggered dialog, only request what was specifically requested
        if (requestedPermissions.video) {
          constraints.video = true;
        }
        if (requestedPermissions.audio) {
          constraints.audio = true;
        }
      }

      // Handle Firefox Android specially
      if (isFirefoxAndroid()) {
        // Use our special Firefox Android permission test
        const result = await testFirefoxAndroidPermissions(constraints);

        // Check if permissions were granted
        const videoGranted = !constraints.video || result.camera === PermissionState.GRANTED;
        const audioGranted = !constraints.audio || result.microphone === PermissionState.GRANTED;

        if (!videoGranted || !audioGranted) {
          // Permission was denied, refresh and show error
          await refresh(true);

          if (
            result.camera === PermissionState.DENIED ||
            result.microphone === PermissionState.DENIED
          ) {
            logger.warn('User denied media permissions');
          }
          return;
        }
      } else {
        // Standard getUserMedia for other browsers
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        // Stop the stream immediately after getting permission
        stream.getTracks().forEach((track) => track.stop());
      }

      // Refresh permissions and close modal
      await refresh();

      setShowPermissionModal(false);

      // Open media test dialog to let user test their camera/microphone
      const testVideo = constraints.video && videoEnabled;
      const testAudio = constraints.audio && audioEnabled;

      if (testVideo || testAudio) {
        mediaTest.openMediaTest({
          video: testVideo,
          audio: testAudio,
        });
      }
    } catch (error: any) {
      // Refresh permissions to get updated state even if request failed
      // Force fallback check for immediate permission state after denial
      await refresh(true);

      // Show appropriate error based on the type
      if (error.name === 'NotAllowedError') {
        // User denied permission - permissions will show as denied on next render
        logger.warn('🚫 User denied media permissions');
      } else if (error.name === 'NotFoundError') {
        logger.warn('📷 No media devices found');
      } else {
        logger.warn('❓ Unknown permission error:', error.message);
      }
    } finally {
      setIsGrantingAccess(false);
    }
  };

  // Handle modal close without granting permissions
  const handleCloseModal = () => {
    iframeLocalStorage.setItem(PERMISSION_MODAL_SHOWN_KEY, 'true');
    setAutoDismissed(true); // Prevent auto-dialog from showing again
    setShowPermissionModal(false);
    setRequestedPermissions({});
  };

  return {
    // Permission state
    permissions,
    isPermissionsLoading,

    // Feature flags
    videoEnabled,
    audioEnabled,

    // Dialog state
    showPermissionModal,
    needsPermissionDialog,
    requestedPermissions,
    isGrantingAccess,

    // Dialog actions
    handleGrantAccess,
    handleCloseModal,

    // Permission request functions
    requestCameraPermission,
    requestMicrophonePermission,
    requestBothPermissions,

    // Media test functionality
    mediaTest,

    // Browser compatibility
    isFirefoxAndroid: isFirefoxAndroid(),
  };
}
